// Readers for the Radisson content-reader payload served by
// /api/v2/rhg/hotel-detail. Every exported shape carries the API path it was
// read from, because the screen renders those paths next to the values.

// Repeatable properties arrive bare when there is one entry and wrapped in
// {values: [...]} when there are several, so anything read as a list has to go
// through here or single-entry hotels silently lose their data.
export const asList = (value) => {
  if (value == null) return [];
  if (Array.isArray(value)) return value.filter((v) => v != null);
  if (typeof value === "object" && "values" in value) {
    const inner = value.values;
    if (Array.isArray(inner)) return inner.filter((v) => v != null);
    return inner == null ? [] : [inner];
  }
  return [value];
};

export const getPath = (root, path) => {
  if (!root || !path) return undefined;
  return path
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".")
    .reduce((acc, key) => (acc == null ? acc : acc[key]), root);
};

export const decodeHTML = (str) => {
  if (!str) return "";
  const el = document.createElement("textarea");
  el.innerHTML = str;
  return el.value;
};

// Outlet and room titles arrive with stray leading/trailing whitespace often
// enough that every heading goes through here.
export const decodeTitle = (str) => decodeHTML(str).trim();

// Long-form copy comes back either as a plain string or as
// {fragments: ["<p>html</p>", ...]} - the same property, both shapes, varying
// by hotel. Reading it straight renders "[object Object]" on every hotel that
// happens to use the second one.
export const readText = (value) => {
  if (value == null) return "";

  const fragments =
    typeof value === "object" && Array.isArray(value.fragments)
      ? value.fragments
      : null;

  const source = fragments
    ? fragments.filter((f) => typeof f === "string").join(" ")
    : typeof value === "string"
      ? value
      : "";

  return decodeHTML(source.replace(/<[^>]+>/g, " "))
    .replace(/\s+/g, " ")
    .trim();
};

// "/" is the feed's own placeholder for "this layout is not offered", and
// shows up alongside real numbers in the same list.
export const isBlankValue = (value) =>
  value == null ||
  value === "" ||
  value === "/" ||
  value === "-" ||
  value === "N/A";

export const toNumber = (value) => {
  if (isBlankValue(value)) return null;
  const n = Number(String(value).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : null;
};

export const formatNumber = (value) => {
  const n = toNumber(value);
  return n == null ? null : n.toLocaleString();
};

const ROOT = {
  hotel: "hotel",
  room: "roomInfo.collection[0]",
  gallery: "gallery.collection[0]",
  restaurants: "restaurants.collection",
  images: "images",
  // Guest-room types and their galleries, synced from the `room` and
  // `galleryRoom` collections.
  rooms: "rooms.collection",
  roomGalleries: "roomGalleries.collection",
  // The `hotelOTA` document: arrival directions and gym / spa copy. Nearly
  // every hotel publishes one now, but the blocks inside it are individually
  // optional - `howToArrive` is always there, `fitness` is not - so "no
  // document" and "document without this block" are reported separately.
  ota: "ota.collection",
  // Editorial copy imported from the supplier spreadsheet. No API carries it,
  // so a hotel with nothing imported returns {collection: [], total: 0} rather
  // than null - see the `sources` map on the response.
  usp: "usp.collection",
  introRooms: "introRooms.collection",
  introDining: "introDining.collection",
  roomFeatures: "roomFeatures.collection",
  fdFeatures: "fdFeatures.collection",
};

export const roots = ROOT;

export const hotelDoc = (payload) => getPath(payload, ROOT.hotel) || null;
export const roomDoc = (payload) => getPath(payload, ROOT.room) || null;
export const galleryDoc = (payload) => getPath(payload, ROOT.gallery) || null;
export const otaDoc = (payload) => asList(getPath(payload, ROOT.ota))[0] || null;
export const restaurantDocs = (payload) =>
  asList(getPath(payload, "restaurants.collection"));

// ─── Identity ─────────────────────────────────────────────────────────────────

export const readIdentity = (payload) => {
  const h = hotelDoc(payload);
  const m = h?.metadata || {};
  const c = h?.content || {};

  return {
    code: payload?.code || m?.tmsHotelCode?.key || "",
    codePath: `${ROOT.hotel}.metadata.tmsHotelCode.key`,
    name: decodeTitle(m?.tmsHotelCode?.description || ""),
    namePath: `${ROOT.hotel}.metadata.tmsHotelCode.description`,
    brand: decodeHTML(m?.brand?.description || ""),
    brandPath: `${ROOT.hotel}.metadata.brand.description`,
    brandCode: m?.brand?.key || "",
    stars: m?.starRating?.key || "",
    starsPath: `${ROOT.hotel}.metadata.starRating.key`,
    status: m?.status?.description || "",
    statusPath: `${ROOT.hotel}.metadata.status.description`,
    hotelTypes: asList(m?.hotelType).map((t) => t?.description).filter(Boolean),
    hotelTypesPath: `${ROOT.hotel}.metadata.hotelType.values[].description`,
    city: decodeHTML(m?.city?.description || ""),
    country: decodeHTML(m?.country?.description || ""),
    continent: m?.continent?.description || "",
    region: m?.region?.description || "",
    linkUrl: h?.linkUrl || "",
    linkUrlPath: `${ROOT.hotel}.linkUrl`,
    title: readText(c?.text?.content?.title),
    titlePath: `${ROOT.hotel}.content.text.content.title`,
    description: readText(c?.text?.content?.shortDescription),
    descriptionPath: `${ROOT.hotel}.content.text.content.shortDescription`,
    heroImage:
      c?.heroImage?.binaryContent?.url || c?.image?.binaryContent?.url || "",
    heroImagePath: `${ROOT.hotel}.content.heroImage.binaryContent.url`,
    lastPublishDate: h?.lastPublishDate || "",
    lastPublishDatePath: `${ROOT.hotel}.lastPublishDate`,
  };
};

// ─── Key figures ──────────────────────────────────────────────────────────────

export const readFigures = (payload) => {
  const rc = roomDoc(payload)?.content || {};
  const metric = rc?.metric || "";

  return [
    {
      key: "guestRooms",
      label: "Guest rooms",
      value: formatNumber(rc?.numOfBedrooms),
      path: `${ROOT.room}.content.numOfBedrooms`,
    },
    {
      key: "meetingRooms",
      label: "Meeting rooms",
      value: formatNumber(rc?.numOfMeetingRooms),
      path: `${ROOT.room}.content.numOfMeetingRooms`,
    },
    {
      key: "maxCapacity",
      label: "Maximum capacity",
      value: formatNumber(rc?.maxCapacity),
      suffix: formatNumber(rc?.maxCapacity) ? "people" : "",
      path: `${ROOT.room}.content.maxCapacity`,
    },
    {
      key: "eventSpace",
      label: "Total event space",
      value: formatNumber(rc?.maxSize),
      suffix: metric,
      path: `${ROOT.room}.content.maxSize`,
      unitPath: `${ROOT.room}.content.metric`,
    },
  ];
};

// ─── Location ─────────────────────────────────────────────────────────────────

export const readLocation = (payload) => {
  const h = hotelDoc(payload);
  const m = h?.metadata || {};
  const c = h?.content || {};
  const parking = c?.parkingPolicy || {};
  const condition = parking?.conditionList || {};

  const lat = c?.coordinates?.latitude;
  const lng = c?.coordinates?.longitude;

  return {
    address: decodeHTML(c?.contactInfo?.address || ""),
    addressPath: `${ROOT.hotel}.content.contactInfo.address`,
    zip: c?.contactInfo?.zip || "",
    zipPath: `${ROOT.hotel}.content.contactInfo.zip`,
    city: decodeHTML(m?.city?.description || ""),
    cityPath: `${ROOT.hotel}.metadata.city.description`,
    country: decodeHTML(m?.country?.description || ""),
    countryPath: `${ROOT.hotel}.metadata.country.description`,
    continent: m?.continent?.description || "",
    continentPath: `${ROOT.hotel}.metadata.continent.description`,
    region: m?.region?.description || "",
    regionPath: `${ROOT.hotel}.metadata.region.description`,
    phone: c?.contactInfo?.phone || "",
    phonePath: `${ROOT.hotel}.content.contactInfo.phone`,
    email: (c?.contactInfo?.email || "").trim(),
    emailPath: `${ROOT.hotel}.content.contactInfo.email`,
    lat,
    lng,
    latPath: `${ROOT.hotel}.content.coordinates.latitude`,
    lngPath: `${ROOT.hotel}.content.coordinates.longitude`,
    settings: asList(m?.location).map((l) => l?.description).filter(Boolean),
    settingsPath: `${ROOT.hotel}.metadata.location.values[].description`,
    poi: asList(m?.poi)
      .map((p) => ({ key: p?.key, label: decodeHTML(p?.description || "") }))
      .filter((p) => p.label),
    poiPath: `${ROOT.hotel}.metadata.poi.values[].description`,
    checkIn: c?.checkIn ? `${c.checkIn.hour}:${c.checkIn.minute}` : "",
    checkInPath: `${ROOT.hotel}.content.checkIn.hour / .minute`,
    checkOut: c?.checkOut ? `${c.checkOut.hour}:${c.checkOut.minute}` : "",
    checkOutPath: `${ROOT.hotel}.content.checkOut.hour / .minute`,
    parking: {
      available: parking?.parkingAvailable,
      availablePath: `${ROOT.hotel}.content.parkingPolicy.parkingAvailable`,
      type: condition?.type?.description || "",
      typePath: `${ROOT.hotel}.content.parkingPolicy.conditionList.type.description`,
      indoorOutdoor: condition?.indoorOutdoor?.description || "",
      indoorOutdoorPath: `${ROOT.hotel}.content.parkingPolicy.conditionList.indoorOutdoor.description`,
      isFree: condition?.isFree,
      isFreePath: `${ROOT.hotel}.content.parkingPolicy.conditionList.isFree`,
      valet: condition?.valet,
      valetPath: `${ROOT.hotel}.content.parkingPolicy.conditionList.valet`,
      maxHeight: condition?.maxHeight || "",
      maxHeightPath: `${ROOT.hotel}.content.parkingPolicy.conditionList.maxHeight`,
      price: condition?.price?.content?.title || "",
      pricePath: `${ROOT.hotel}.content.parkingPolicy.conditionList.price.content.title`,
    },
  };
};

export const hasCoordinates = (location) =>
  location?.lat != null &&
  location?.lng != null &&
  !Number.isNaN(Number(location.lat)) &&
  !Number.isNaN(Number(location.lng));

// ─── Meeting rooms and layouts ────────────────────────────────────────────────

// Fixed display order so the matrix columns line up across hotels. Anything
// the feed publishes outside this list is appended in first-seen order.
const LAYOUT_ORDER = [
  "THE",
  "CLS",
  "BRD",
  "USH",
  "CAB",
  "RND",
  "REC",
  "LUN",
  "DIN",
  "DRD",
];

export const readMeetingRooms = (payload) => {
  const rc = roomDoc(payload)?.content || {};
  const listPath = `${ROOT.room}.content.meetingRooms.content.meetingRoomList.values[]`;
  const rooms = asList(rc?.meetingRooms?.content?.meetingRoomList).map(
    (entry, index) => {
      const c = entry?.content || {};
      const text = c?.text?.content || {};
      const shapes = asList(c?.meetingRoomShapeList).map((s) => ({
        key: s?.type?.key || null,
        label: s?.type?.description || null,
        title: s?.type?.title || null,
        value: s?.value,
      }));

      return {
        index,
        name: decodeTitle(text?.title) || `Room ${index + 1}`,
        namePath: `${listPath}[${index}].content.text.content.title`,
        size: text?.meetingRoomSize || "",
        sizePath: `${listPath}[${index}].content.text.content.meetingRoomSize`,
        description: readText(text?.description),
        descriptionPath: `${listPath}[${index}].content.text.content.description`,
        image: c?.mainImage?.binaryContent?.url || "",
        imagePath: `${listPath}[${index}].content.mainImage.binaryContent.url`,
        thumbnail:
          c?.mainImage?.externalContent?.metadata?.urlThumbnail ||
          c?.mainImage?.binaryContent?.url ||
          "",
        altText: decodeHTML(c?.mainImage?.metadata?.altText || ""),
        shapes,
        shapesPath: `${listPath}[${index}].content.meetingRoomShapeList.values[]`,
        // The feed strips the layout taxonomy from the first two room
        // documents of every response, so their capacities arrive as bare
        // numbers with nothing saying which setup each one is for.
        shapesUnlabelled: shapes.length > 0 && shapes.every((s) => !s.key),
        features: asList(c?.meetingRoomDetailsList)
          .map((d) => d?.type?.description)
          .filter(Boolean),
        featuresPath: `${listPath}[${index}].content.meetingRoomDetailsList.values[].type.description`,
        tour:
          c?.gallery?.content?.multimediaAndImmersive?.immersiveContentIframe
            ?.url || "",
        tourPath: `${listPath}[${index}].content.gallery.content.multimediaAndImmersive.immersiveContentIframe.url`,
        images: asList(c?.gallery?.content?.multimedia)
          .flat()
          .filter((img) => img?.binaryContent?.url),
        imagesPath: `${listPath}[${index}].content.gallery.content.multimedia.values[]`,
      };
    },
  );

  const seen = new Map();
  rooms.forEach((room) =>
    room.shapes.forEach((s) => {
      if (s.key && !seen.has(s.key)) seen.set(s.key, s.label || s.key);
    }),
  );
  const layouts = [...seen.entries()]
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => {
      const ai = LAYOUT_ORDER.indexOf(a.key);
      const bi = LAYOUT_ORDER.indexOf(b.key);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

  return { rooms, layouts, listPath, metric: rc?.metric || "" };
};

export const capacityFor = (room, layoutKey) => {
  const match = room.shapes.find((s) => s.key === layoutKey);
  return match ? toNumber(match.value) : null;
};

// ─── Meeting and event features ───────────────────────────────────────────────

// Upstream wires the two headline counts into the same list as the real
// features, tagged "[insert number below]" in their title. They are already
// shown as key figures, so they are split out rather than repeated as tags.
const COUNT_FEATURE_KEYS = new Set(["meeting-rooms", "maximum-capacity"]);

export const readMeetingFeatures = (payload) => {
  const rc = roomDoc(payload)?.content || {};
  const path = `${ROOT.room}.content.features.content.meetingFeature.values[]`;
  // Entries arrive as `{}`, as a bare `[]`, and as `{text: "3"}` with the
  // feature taxonomy stripped - the same omission that hits the room layout
  // types. Anything with no label is pulled out rather than rendered as a
  // nameless tag.
  const all = asList(rc?.features?.content?.meetingFeature)
    .map((v) => ({
      key: (v && v.feature?.key) || "",
      label: decodeHTML((v && v.feature?.description) || ""),
      title: decodeHTML((v && v.feature?.title) || ""),
      text: v && v.text != null ? String(v.text) : "",
    }))
    .filter((f) => f.label || f.text);

  const labelled = all.filter((f) => f.label);

  return {
    path,
    heading: readText(rc?.features?.content?.text?.content?.title),
    headingPath: `${ROOT.room}.content.features.content.text.content.title`,
    link: rc?.features?.linkUrl || "",
    linkPath: `${ROOT.room}.content.features.linkUrl`,
    counts: labelled.filter((f) => COUNT_FEATURE_KEYS.has(f.key) && f.text),
    features: labelled.filter((f) => !COUNT_FEATURE_KEYS.has(f.key)),
    unlabelled: all.filter((f) => !f.label),
    hotelDescription: readText(rc?.text?.content?.hotelDescription),
    hotelDescriptionPath: `${ROOT.room}.content.text.content.hotelDescription`,
    rfpEmail: rc?.emailRFPToALL || "",
    rfpEmailPath: `${ROOT.room}.content.emailRFPToALL`,
  };
};

export const readHotelServices = (payload) => {
  const c = hotelDoc(payload)?.content || {};
  return {
    path: `${ROOT.hotel}.content.service.values[].service.description`,
    services: asList(c?.service)
      .map((v) => decodeHTML(((v && v.service) || v)?.description || ""))
      .filter(Boolean),
    categoriesPath: `${ROOT.hotel}.content.serviceCategory.values[].description`,
    categories: asList(c?.serviceCategory)
      .map((v) => decodeHTML((v && v.description) || ""))
      .filter(Boolean),
    accessibilityPath: `${ROOT.hotel}.content.accessibilityFeature.values[]`,
    accessibility: asList(c?.accessibilityFeature)
      .map((a) => ({
        label: decodeHTML((a && a.feature?.description) || ""),
        number: (a && a.number) || "",
      }))
      .filter((a) => a.label),
  };
};

// ─── Food and beverage ────────────────────────────────────────────────────────

const PDF_RE = /\.pdf(\?|$)/i;

export const readOutlets = (payload) => {
  const base = "restaurants.collection";

  return restaurantDocs(payload)
    .filter((doc) => doc?.metadata?.tmsHotelCode?.key)
    .map((doc, index) => {
      const c = doc?.content || {};
      const path = `${base}[${index}]`;
      const links = asList(c?.linkList);

      return {
        index,
        path,
        name: decodeTitle(c?.text?.content?.title) || `Outlet ${index + 1}`,
        namePath: `${path}.content.text.content.title`,
        headline: readText(c?.text?.content?.mainTitle),
        headlinePath: `${path}.content.text.content.mainTitle`,
        description:
          readText(c?.text?.content?.mainDescription) ||
          readText(c?.text?.content?.description) ||
          readText(c?.seo?.content?.description),
        descriptionPath: `${path}.content.text.content.mainDescription`,
        isBreakfast: doc?.metadata?.isBreakfast === true,
        isBreakfastPath: `${path}.metadata.isBreakfast`,
        cuisine: asList(c?.typeCuisine)
          .map((t) => decodeHTML(t?.description || ""))
          .filter(Boolean),
        cuisinePath: `${path}.content.typeCuisine.values[].description`,
        services: asList(c?.serviceList)
          .map((s) => decodeHTML(s?.description || ""))
          .filter(Boolean),
        servicesPath: `${path}.content.serviceList.values[].description`,
        floor: decodeHTML(c?.location?.description || ""),
        floorPath: `${path}.content.location.description`,
        hours: asList(c?.open).map((o) => ({
          type: decodeHTML(o?.type?.description || ""),
          fromDay: decodeHTML(o?.startDayWeek?.description || ""),
          toDay: decodeHTML(o?.endDayWeek?.description || ""),
          prep: decodeHTML(o?.prep?.description || ""),
          from: [o?.startHour, o?.startMinute].filter(Boolean).join(":"),
          to: [o?.endHour, o?.endMinute].filter(Boolean).join(":"),
          note: decodeHTML(o?.text?.content?.description || ""),
        })),
        hoursPath: `${path}.content.open.values[]`,
        // Every asset in this feed reports mimeType image/jpeg, menu PDFs
        // included, so the extension is the only reliable signal.
        menus: links
          .map((l) => ({
            label: decodeHTML(l?.content?.text || "Menu"),
            url: l?.content?.pdf?.binaryContent?.url || "",
          }))
          .filter((m) => m.url && PDF_RE.test(m.url)),
        menusPath: `${path}.content.linkList.values[].content.pdf.binaryContent.url`,
        bookLink: c?.bookLink?.content?.externalLink || "",
        bookLabel: decodeHTML(c?.bookLink?.content?.text || "Book a table"),
        bookLinkPath: `${path}.content.bookLink.content.externalLink`,
        phone: (c?.appBookLink?.content?.externalLink || "").replace(
          /^tel:/,
          "",
        ),
        phonePath: `${path}.content.appBookLink.content.externalLink`,
        emailLink: (c?.appEmailInfo?.content?.externalLink || "").replace(
          /^mailto:/,
          "",
        ),
        emailLinkPath: `${path}.content.appEmailInfo.content.externalLink`,
        pageUrl: doc?.linkUrl || "",
        pageUrlPath: `${path}.linkUrl`,
        hero:
          c?.heroSimpleImage?.binaryContent?.url ||
          c?.multimedia?.binaryContent?.url ||
          "",
        heroPath: `${path}.content.heroSimpleImage.binaryContent.url`,
        images: asList(c?.gallery?.content?.multimedia)
          .flat()
          .filter((img) => img?.binaryContent?.url),
        imagesPath: `${path}.content.gallery.content.multimedia.values[]`,
      };
    });
};

// ─── Galleries ────────────────────────────────────────────────────────────────

const toImage = (img, path) => ({
  url: img?.binaryContent?.url || "",
  thumb:
    img?.externalContent?.metadata?.urlThumbnail ||
    img?.binaryContent?.url ||
    "",
  alt: decodeHTML(
    img?.metadata?.altText ||
      img?.externalContent?.metadata?.altText ||
      "",
  ),
  width: img?.externalContent?.metadata?.width || "",
  height: img?.externalContent?.metadata?.height || "",
  id: img?.externalContent?.metadata?.imageID || img?.binaryContent?.fileName,
  path,
});

const dedupeImages = (images) => {
  const seen = new Set();
  return images.filter((img) => {
    if (!img.url || seen.has(img.url)) return false;
    seen.add(img.url);
    return true;
  });
};

const MEETING_CATEGORY_KEYS = new Set([
  "gallery-meetings-and-events",
  "gallery-meeting-rooms",
]);

// Four sources carry meeting-room photography and no hotel publishes all of
// them, so they are merged (by URL) rather than picked between.
export const readMeetingGallery = (payload, meetingRooms) => {
  const rc = roomDoc(payload)?.content || {};
  const sources = [];

  const dedicatedPath = `${ROOT.room}.content.galleryMeetingsList.content.gallery.content.multimedia.values[]`;
  const dedicated = asList(
    rc?.galleryMeetingsList?.content?.gallery?.content?.multimedia,
  )
    .flat()
    .filter((img) => img?.binaryContent?.url)
    .map((img) => toImage(img, dedicatedPath));
  if (dedicated.length) {
    sources.push({
      label: "Meetings gallery",
      path: dedicatedPath,
      images: dedicated,
    });
  }

  const categorised = [];
  asList(galleryDoc(payload)?.content?.gallery).forEach((entry, i) => {
    const key = entry?.content?.category?.key;
    if (!MEETING_CATEGORY_KEYS.has(key)) return;
    const p = `${ROOT.gallery}.content.gallery.values[${i}].content.multimedia.values[]`;
    asList(entry?.content?.multimedia)
      .flat()
      .filter((img) => img?.binaryContent?.url)
      .forEach((img) => categorised.push(toImage(img, p)));
  });
  if (categorised.length) {
    sources.push({
      label: "Hotel gallery · Meetings & Events",
      path: `${ROOT.gallery}.content.gallery.values[].content.category.key = gallery-meetings-and-events`,
      images: categorised,
    });
  }

  const perRoom = [];
  (meetingRooms || []).forEach((room) => {
    if (room.image) {
      perRoom.push({
        ...toImage(
          { binaryContent: { url: room.image }, metadata: { altText: room.altText } },
          room.imagePath,
        ),
        thumb: room.thumbnail || room.image,
        caption: room.name,
      });
    }
    room.images.forEach((img) =>
      perRoom.push({ ...toImage(img, room.imagesPath), caption: room.name }),
    );
  });
  if (perRoom.length) {
    sources.push({
      label: "Per meeting room",
      path: `${ROOT.room}.content.meetingRooms.content.meetingRoomList.values[].content.mainImage`,
      images: perRoom,
    });
  }

  const picked = getPath(payload, "images.meetingRoom");
  const pickedUrl = picked?.links?.cdnLinks?.[0];
  if (pickedUrl) {
    sources.push({
      label: "ICE Portal selection",
      path: "images.meetingRoom.links.cdnLinks[0]",
      images: [
        {
          url: pickedUrl,
          thumb: picked?.links?.thumbnailLink || pickedUrl,
          alt: picked?.category?.[0]?.otaCategory || "Meeting room",
          id: picked?.assetId,
          path: "images.meetingRoom.links.cdnLinks[0]",
        },
      ],
    });
  }

  const all = dedupeImages(sources.flatMap((s) => s.images));
  return { sources, images: all };
};

export const readHotelGalleryCategories = (payload) => {
  const doc = galleryDoc(payload);
  const basePath = `${ROOT.gallery}.content.gallery.values[]`;

  return asList(doc?.content?.gallery)
    .map((entry, i) => {
      const images = asList(entry?.content?.multimedia)
        .flat()
        .filter((img) => img?.binaryContent?.url)
        .map((img) =>
          toImage(img, `${basePath}[${i}].content.multimedia.values[]`),
        );
      return {
        index: i,
        key: entry?.content?.category?.key || null,
        label: entry?.content?.category?.description || null,
        path: `${basePath}[${i}]`,
        images,
      };
    })
    .filter((g) => g.images.length);
};

export const readPickedImages = (payload) => {
  const images = getPath(payload, "images") || {};
  return Object.entries(images)
    .map(([slot, asset]) => ({
      slot,
      path: `images.${slot}.links.cdnLinks[0]`,
      url: asset?.links?.cdnLinks?.[0] || "",
      thumb: asset?.links?.thumbnailLink || "",
      category: asset?.category?.[0]?.otaCategory || "",
      assetId: asset?.assetId || "",
      keywords: asset?.autoKeywords || [],
    }))
    .filter((i) => i.url);
};

export const readImmersiveTour = (payload) => {
  const doc = galleryDoc(payload);
  return {
    url: doc?.content?.highlighted || "",
    path: `${ROOT.gallery}.content.highlighted`,
  };
};

// ─── Guest rooms ──────────────────────────────────────────────────────────────

// The editorial blocks are multi-paragraph HTML, already sanitised server-side
// by the importer. readText() flattens everything to one string, which is right
// for a one-line description and wrong for a three-paragraph intro, so this
// keeps the paragraph breaks and still hands back plain strings - no
// dangerouslySetInnerHTML anywhere on this screen.
export const readParagraphs = (value) => {
  if (value == null) return [];

  const source =
    typeof value === "object" && Array.isArray(value.fragments)
      ? value.fragments.filter((f) => typeof f === "string").join("")
      : typeof value === "string"
        ? value
        : "";
  if (!source) return [];

  return source
    .split(/<\/p>|<\/div>|<br\s*\/?>/i)
    .map((chunk) => decodeHTML(chunk.replace(/<[^>]+>/g, " ")).replace(/\s+/g, " ").trim())
    .filter(Boolean);
};

// Bed types arrive bare for a single option and wrapped in {values: [...]} for
// several, the same quirk as every other repeatable property. For GBEDLONBSH it
// is bare on 5 rooms and wrapped on 4, so reading it either way alone loses
// half the hotel.
const readBeds = (content) =>
  asList(content?.bedType)
    .map((b) => ({
      label: decodeHTML(b?.type?.description || ""),
      number: isBlankValue(b?.number) ? "" : String(b.number),
    }))
    .filter((b) => b.label);

export const readRoomsIntro = (payload) => {
  const entry = asList(getPath(payload, ROOT.introRooms))[0] || null;
  return {
    title: decodeTitle(entry?.title || ""),
    titlePath: `${ROOT.introRooms}[0].title`,
    paragraphs: readParagraphs(entry?.description),
    paragraphsPath: `${ROOT.introRooms}[0].description`,
    source: entry?.source || "",
    hasEntry: entry != null,
  };
};

// Joins each room type to its gallery on metadata.tmsRoomCode.key. Most
// galleries are "dynamic" - 5,280 of 5,729 upstream carry no multimedia at all
// because the images are assembled downstream from ICE Portal - so an empty
// gallery is the normal case and is reported rather than hidden.
export const readRooms = (payload) => {
  const galleries = new Map();
  asList(getPath(payload, ROOT.roomGalleries)).forEach((doc) => {
    const key = doc?.metadata?.tmsRoomCode?.key;
    if (key) galleries.set(key, doc);
  });

  const rooms = asList(getPath(payload, ROOT.rooms)).map((doc, i) => {
    const m = doc?.metadata || {};
    const c = doc?.content || {};
    const key = m?.tmsRoomCode?.key || "";
    const gallery = galleries.get(key) || null;
    const gc = gallery?.content || {};
    const base = `${ROOT.rooms}[${i}]`;

    const images = asList(gc?.multimedia)
      .map((img) => toImage(img, `${ROOT.roomGalleries}[].content.multimedia.values[]`))
      .filter((img) => img.url);

    return {
      index: i,
      key,
      keyPath: `${base}.metadata.tmsRoomCode.key`,
      name: decodeTitle(m?.tmsRoomCode?.description || ""),
      namePath: `${base}.metadata.tmsRoomCode.description`,
      // Upstream publishes every number as a string, so these stay strings and
      // are only coerced for display.
      size: formatNumber(c?.size),
      metric: c?.metric || "",
      sizePath: `${base}.content.size`,
      maxAdults: toNumber(c?.occupancy?.maxAdults),
      maxChildren: toNumber(c?.occupancy?.maxChildren),
      occupancyPath: `${base}.content.occupancy`,
      beds: readBeds(c),
      bedsPath: `${base}.content.bedType`,
      amenities: asList(c?.service)
        .map((v) => decodeHTML(v?.description || ""))
        .filter(Boolean),
      amenitiesPath: `${base}.content.service.values[].description`,
      paragraphs: readParagraphs(c?.text?.content?.description),
      descriptionPath: `${base}.content.text.content.description`,
      hero: toImage(c?.image, `${base}.content.image`),
      images,
      imagesPath: `${ROOT.roomGalleries}[].content.multimedia`,
      hasGallery: gallery != null,
      isGalleryDynamic: gc?.isGalleryDynamic === true,
    };
  });

  return {
    rooms,
    intro: readRoomsIntro(payload),
    path: ROOT.rooms,
    // Surfaced because "no images" has two very different causes and the
    // difference matters when someone is checking why a room looks bare.
    withGallery: rooms.filter((r) => r.images.length).length,
    dynamic: rooms.filter((r) => r.hasGallery && r.isGalleryDynamic).length,
    missingGallery: rooms.filter((r) => !r.hasGallery).length,
  };
};

// ─── OTA: arrival directions and wellness ─────────────────────────────────────

const OTA = `${ROOT.ota}[0].content`;

// Gym / spa copy. The description is a {fragments: [...]} array here, which is
// the only place in the API that shape appears, so it goes through
// readParagraphs() rather than being read as a field.
export const readFitness = (payload) => {
  const doc = otaDoc(payload);
  const fitness = doc?.content?.fitness?.content || null;

  return {
    hasDoc: doc != null,
    title: decodeTitle(fitness?.title || ""),
    titlePath: `${OTA}.fitness.content.title`,
    paragraphs: readParagraphs(fitness?.description),
    paragraphsPath: `${OTA}.fitness.content.description.fragments[]`,
    hasEntry: fitness != null,
  };
};

// Arrival directions: an intro block, then one group per origin ("From
// Copenhagen Airport") carrying a transport-mode taxonomy icon and a list of
// legs ("By bus:", "On foot:").
export const readArrival = (payload) => {
  const doc = otaDoc(payload);
  const arrive = doc?.content?.howToArrive || null;
  const c = arrive?.content || null;
  const intro = c?.text?.content || null;
  const contact = c?.contactLink?.content || null;

  const groups = asList(c?.list)
    .map((entry, i) => {
      const text = entry?.content?.text?.content || null;
      const icon = entry?.content?.iconClass || null;
      const groupPath = `${OTA}.howToArrive.content.list.values[${i}]`;

      return {
        index: i,
        title: decodeTitle(text?.title || ""),
        mode: decodeTitle(icon?.description || icon?.title || ""),
        iconKey: icon?.key || "",
        path: groupPath,
        // The directions are a plain string on some legs and
        // {fragments: [...]} on others within the same list.
        legs: asList(text?.from)
          .map((leg, j) => ({
            index: j,
            label: decodeTitle(leg?.subtitle || "").replace(/:$/, ""),
            paragraphs: readParagraphs(leg?.description),
            path: `${groupPath}.content.text.content.from.values[${j}]`,
          }))
          .filter((leg) => leg.label || leg.paragraphs.length),
      };
    })
    .filter((g) => g.title || g.legs.length);

  return {
    hasDoc: doc != null,
    title: decodeTitle(intro?.title || ""),
    titlePath: `${OTA}.howToArrive.content.text.content.title`,
    paragraphs: readParagraphs(intro?.description),
    paragraphsPath: `${OTA}.howToArrive.content.text.content.description.fragments[]`,
    groups,
    groupsPath: `${OTA}.howToArrive.content.list.values[]`,
    hotelPage: arrive?.linkUrl || "",
    hotelPagePath: `${OTA}.howToArrive.linkUrl`,
    contactLabel: decodeTitle(contact?.text || contact?.title || ""),
    contactPage: contact?.seo?.linkUrl || c?.seo?.linkUrl || "",
    contactPagePath: `${OTA}.howToArrive.content.contactLink.content.seo.linkUrl`,
    hasEntry: arrive != null,
  };
};

// ─── Imported editorial copy ──────────────────────────────────────────────────

// Unique selling points: 1:N, typically three per hotel. `subtitle` and
// `shortDescription` are the literal word "Empty" in the vast majority of rows
// upstream and the importer normalises those to null, so most cards are a
// title alone.
export const readUsps = (payload) =>
  asList(getPath(payload, ROOT.usp))
    .map((entry, i) => ({
      index: i,
      title: decodeTitle(entry?.title || ""),
      subtitle: decodeTitle(entry?.subtitle || ""),
      description: readText(entry?.shortDescription),
      // The spreadsheet's own component label. Free text with ~1,500 variants
      // upstream, so it is shown for reference and never grouped on.
      label: decodeTitle(entry?.label || ""),
      path: `${ROOT.usp}[${i}]`,
    }))
    .filter((u) => u.title || u.description);

export const readRoomFeatures = (payload) => {
  const entry = asList(getPath(payload, ROOT.roomFeatures))[0] || null;
  return {
    features: (Array.isArray(entry?.features) ? entry.features : []).filter(
      (f) => typeof f === "string" && f,
    ),
    path: `${ROOT.roomFeatures}[0].features[]`,
    hasEntry: entry != null,
  };
};

export const readDiningIntro = (payload) =>
  asList(getPath(payload, ROOT.introDining))
    .map((entry, i) => ({
      index: i,
      // "R&B", "Dining", or occasionally an outlet name - kept verbatim.
      scope: decodeTitle(entry?.scope || ""),
      title: decodeTitle(entry?.title || ""),
      paragraphs: readParagraphs(entry?.description),
      path: `${ROOT.introDining}[${i}]`,
    }))
    .filter((d) => d.title || d.paragraphs.length);

export const readFdFeatures = (payload) =>
  asList(getPath(payload, ROOT.fdFeatures))
    .map((entry, i) => ({
      index: i,
      scope: decodeTitle(entry?.scope || ""),
      features: (Array.isArray(entry?.features) ? entry.features : []).filter(
        (f) => typeof f === "string" && f,
      ),
      path: `${ROOT.fdFeatures}[${i}].features[]`,
    }))
    .filter((f) => f.features.length);

// Whether a part came from the spreadsheet, from the API, or is simply not
// published. The endpoint reports this per part, and it is the only way to tell
// "this hotel has no selling points" from "nothing has been imported yet".
export const readSource = (payload, key) =>
  (payload?.sources && payload.sources[key]) || "";
